import { Component, ElementRef, ViewChild, AfterViewInit, output, OnDestroy, input, effect, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

interface CarMaterials {
  paint: THREE.MeshStandardMaterial;
  window: THREE.MeshStandardMaterial;
  bumper: THREE.MeshStandardMaterial;
  tire: THREE.MeshStandardMaterial;
  rim: THREE.MeshStandardMaterial;
  light: THREE.MeshStandardMaterial;
  tailLight: THREE.MeshStandardMaterial;
  grill: THREE.MeshStandardMaterial;
}

@Component({
  selector: 'app-vehicle-three-d',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="vehicle-wrapper" [style.height]="height()">
      <div #rendererContainer class="renderer"></div>
      <div *ngIf="loading" class="loading-overlay">
        <div class="spinner-border text-light" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div class="ms-2 text-white">Loading {{vehicleType() | titlecase}}...</div>
      </div>
      <div *ngIf="loadError" class="error-overlay">
         <p class="text-white bg-danger p-2 rounded">Failed to load {{vehicleType()}}. Using fallback.</p>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    .vehicle-wrapper {
      width: 100%;
      position: relative;
      background: linear-gradient(180deg, #e9eef6, #f7fafc);
      border-radius: 8px;
      overflow: hidden;
    }
    .renderer {
      width: 100%;
      height: 100%;
    }
    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.7);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10;
    }
    .error-overlay {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 20;
      pointer-events: none;
    }
  `]
})
export class VehicleThreeDComponent implements AfterViewInit, OnDestroy {
  @ViewChild('rendererContainer') rendererContainer!: ElementRef<HTMLDivElement>;
  partsSelected = output<string[]>();
  initialSelectedParts = input<string[]>([]);
  vehicleType = input<'sedan' | 'pickup' | 'suv' | 'hatchback'>('sedan');
  height = input<string>('480px');
  readOnly = input<boolean>(false);

  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private controls!: OrbitControls;
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private animationId: number | null = null;
  private currentModel: THREE.Object3D | null = null;

  loading = true;
  loadError = false;
  selectedPartIds = new Set<string>();
  interactableObjects: THREE.Object3D[] = [];

  private defaultColor = 0xeeeeee;
  private hoverColor = 0xa0c4ff;
  private selectedColor = 0xdc3545;
  private isInitialized = false;

  constructor() {
    effect(() => {
      if (this.isInitialized) this.loadVehicle();
    });

    effect(() => {
      const parts = this.initialSelectedParts();
      if (this.isInitialized) {
        this.selectedPartIds.clear();
        parts.forEach(id => this.selectedPartIds.add(id));
        this.updateColors();
      }
    });
  }

  ngAfterViewInit() {
    this.initThree();
    this.createEnvironment();
    this.loadVehicle();
    this.animate();
    this._loadInitialParts();
    this._addEventListeners();
    this.isInitialized = true;
  }

  ngOnDestroy() {
    if (this.animationId) cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.onWindowResizeBound);
    this._removeEventListeners();
    this.disposeScene(this.scene);
    this.controls?.dispose();
    this.renderer?.dispose();
  }

  private onWindowResizeBound = this.onWindowResize.bind(this);
  private onMouseMoveBound = (e: MouseEvent) => this.onMouseMove(e);
  private onMouseClickBound = (e: MouseEvent) => this.onMouseClick(e);

  private _loadInitialParts() {
    if (this.initialSelectedParts().length) {
      this.initialSelectedParts().forEach(id => this.selectedPartIds.add(id));
    }
  }

  private _addEventListeners() {
    window.addEventListener('resize', this.onWindowResizeBound);
    const el = this.rendererContainer.nativeElement;
    el.addEventListener('mousemove', this.onMouseMoveBound);
    el.addEventListener('click', this.onMouseClickBound);
  }

  private _removeEventListeners() {
    const el = this.rendererContainer?.nativeElement;
    if (el) {
      el.removeEventListener('mousemove', this.onMouseMoveBound);
      el.removeEventListener('click', this.onMouseClickBound);
    }
  }

  /* -------------------- CORE SETUP -------------------- */
  private initThree() {
    const container = this.rendererContainer.nativeElement;
    const w = container.clientWidth;
    const h = container.clientHeight;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf2f6fb);

    this.camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 200);
    this.camera.position.set(5, 3, 5);
    this.camera.lookAt(0, 0.5, 0);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    this.renderer.setSize(w, h);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;

    container.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.06;
    this.controls.minDistance = 2;
    this.controls.maxDistance = 15;
  }

  /* -------------------- SCENE -------------------- */
  private createEnvironment() {
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(50, 50),
      new THREE.MeshStandardMaterial({ color: 0xffffff })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.scene.add(ground);

    const hemi = new THREE.HemisphereLight(0xf7fbff, 0xdfe6ee, 0.9);
    this.scene.add(hemi);

    const dir = new THREE.DirectionalLight(0xffffff, 1.2);
    dir.position.set(5, 10, 4);
    dir.castShadow = true;
    this.scene.add(dir);

    const fill = new THREE.DirectionalLight(0xffffff, 0.5);
    fill.position.set(-5, 5, -5);
    this.scene.add(fill);
  }

  /* -------------------- VEHICLE LOADING -------------------- */
  private loadVehicle() {
    this.loading = true;
    this.loadError = false;

    if (this.currentModel) {
      this.scene.remove(this.currentModel);
      this.disposeScene(this.currentModel);
      this.currentModel = null;
    }
    this.interactableObjects = [];

    this.loadGLTFModel().catch((err) => {
      console.warn(`Failed to load ${this.vehicleType()} GLTF:`, err);
      this.loadError = true;
      this.createCar();
      this.loading = false;
      setTimeout(() => this.loadError = false, 3000);
    });
  }

  private loadGLTFModel(): Promise<void> {
    const loader = new GLTFLoader();
    const path = `assets/vehicle/${this.vehicleType()}/scene.gltf`;
    return new Promise((resolve, reject) => {
      loader.load(path, (gltf) => this._onGltfLoaded(gltf, resolve), undefined, reject);
    });
  }

  private _onGltfLoaded(gltf: any, resolve: () => void) {
    const model = gltf.scene;
    this.currentModel = model;
    this._adjustModelPositionAndScale(model);
    model.traverse((child: THREE.Object3D) => this._setupGltfMesh(child));
    this.scene.add(model);
    this.updateColors();
    this.loading = false;
    resolve();
  }

  private _adjustModelPositionAndScale(model: THREE.Object3D) {
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const scale = 4 / Math.max(size.x, size.y, size.z);
    model.scale.set(scale, scale, scale);
    model.position.x = -center.x * scale;
    model.position.y = -box.min.y * scale + 0.1;
    model.position.z = -center.z * scale;
  }

  private _setupGltfMesh(child: THREE.Object3D) {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    const id = child.name?.trim() || `part_${child.id}`;
    this._cloneAndSetupMaterials(mesh);
    this.registerInteractable(mesh, id);
  }

  private _cloneAndSetupMaterials(mesh: THREE.Mesh) {
    if (!mesh.material) return;
    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map(m => m.clone())
      : mesh.material.clone();
    this._setMeshDefaultColor(mesh);
  }

  private _setMeshDefaultColor(mesh: THREE.Mesh) {
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach(m => (m as THREE.MeshStandardMaterial).color?.setHex(this.defaultColor));
  }

  /* -------------------- PROCEDURAL VEHICLE (FALLBACK) -------------------- */
  private _initCarMaterials(): CarMaterials {
    return {
      paint: new THREE.MeshStandardMaterial({ color: 0xe74c3c, roughness: 0.5, metalness: 0.1 }),
      window: new THREE.MeshStandardMaterial({ color: 0xbdc3c7, roughness: 0.2, metalness: 0.5, transparent: true, opacity: 0.7 }),
      bumper: new THREE.MeshStandardMaterial({ color: 0x2c3e50, roughness: 0.9 }),
      tire: new THREE.MeshStandardMaterial({ color: 0x2c3e50, roughness: 0.9 }),
      rim: new THREE.MeshStandardMaterial({ color: 0x95a5a6, roughness: 0.5 }),
      light: new THREE.MeshStandardMaterial({ color: 0xf1c40f, emissive: 0xf1c40f, emissiveIntensity: 0.5 }),
      tailLight: new THREE.MeshStandardMaterial({ color: 0xff0000, emissive: 0xaa0000, emissiveIntensity: 0.5 }),
      grill: new THREE.MeshStandardMaterial({ color: 0x2c3e50, roughness: 0.9 })
    };
  }

  private _createCarPart(carGroup: THREE.Group, geometry: THREE.BufferGeometry, id: string, name: string, material: THREE.Material) {
    const mesh = new THREE.Mesh(geometry, material.clone());
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData = { id, name, isPart: true };
    this.registerInteractable(mesh, id);
    carGroup.add(mesh);
    return mesh;
  }

  private createCar() {
    const carGroup = new THREE.Group();
    this.currentModel = carGroup;
    const mats = this._initCarMaterials();

    this._buildCarChassis(carGroup, mats);
    this._buildCarBlocks(carGroup, mats);
    this._buildCarDoors(carGroup, mats);
    this._buildCarCabinAndPillars(carGroup, mats);
    this._buildCarWindows(carGroup, mats);
    this._buildCarBumpersAndGrill(carGroup, mats);
    this._buildCarLights(carGroup, mats);
    this._buildCarWheels(carGroup, mats);
    this._buildCarAccessories(carGroup);

    carGroup.position.y = 0.2;
    this.scene.add(carGroup);
  }

  private _buildCarChassis(carGroup: THREE.Group, mats: CarMaterials) {
    const chassis = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.5, 3.8), new THREE.MeshStandardMaterial({ color: 0x111111 }));
    chassis.position.set(0, 0.4, 0);
    carGroup.add(chassis);

    const hood = this._createCarPart(carGroup, new THREE.BoxGeometry(1.8, 0.1, 1.0), 'hood', 'Hood', mats.paint);
    hood.position.set(0, 0.8, 1.5);

    const trunk = this._createCarPart(carGroup, new THREE.BoxGeometry(1.8, 0.1, 0.8), 'trunk', 'Trunk', mats.paint);
    trunk.position.set(0, 0.8, -1.6);
  }

  private _buildCarBlocks(carGroup: THREE.Group, mats: CarMaterials) {
    const front = this._createCarPart(carGroup, new THREE.BoxGeometry(1.8, 0.8, 1.0), 'front_section', 'Front Section', mats.paint);
    front.position.set(0, 0.4, 1.5);

    const rear = this._createCarPart(carGroup, new THREE.BoxGeometry(1.8, 0.6, 0.8), 'rear_section', 'Rear Section', mats.paint);
    rear.position.set(0, 0.4, -1.6);
  }

  private _buildCarDoors(carGroup: THREE.Group, mats: CarMaterials) {
    const doorL = this._createCarPart(carGroup, new THREE.BoxGeometry(0.1, 0.8, 2.2), 'door_left', 'Left Door', mats.paint);
    doorL.position.set(0.85, 0.4, 0);

    const doorR = this._createCarPart(carGroup, new THREE.BoxGeometry(0.1, 0.8, 2.2), 'door_right', 'Right Door', mats.paint);
    doorR.position.set(-0.85, 0.4, 0);

    const floor = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.8, 2.2), mats.paint);
    floor.position.set(0, 0.4, 0);
    carGroup.add(floor);
  }

  private _buildCarCabinAndPillars(carGroup: THREE.Group, mats: CarMaterials) {
    const roof = this._createCarPart(carGroup, new THREE.BoxGeometry(1.4, 0.1, 1.6), 'roof', 'Roof', mats.paint);
    roof.position.set(0, 1.5, 0);

    const pF = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.7, 0.2), mats.paint);
    pF.position.set(0, 1.15, 0.9);
    pF.rotation.x = -0.2;
    carGroup.add(pF);

    const pR = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.7, 0.2), mats.paint);
    pR.position.set(0, 1.15, -0.9);
    pR.rotation.x = 0.1;
    carGroup.add(pR);

    const pM = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.7, 0.3), mats.paint);
    pM.position.set(0, 1.15, 0);
    carGroup.add(pM);
  }

  private _buildCarWindows(carGroup: THREE.Group, mats: CarMaterials) {
    const front = this._createCarPart(carGroup, new THREE.BoxGeometry(1.3, 0.6, 0.05), 'glass_front', 'Windshield', mats.window);
    front.position.set(0, 1.15, 1.0);
    front.rotation.x = -0.2;

    const rear = this._createCarPart(carGroup, new THREE.BoxGeometry(1.3, 0.6, 0.05), 'glass_rear', 'Rear Window', mats.window);
    rear.position.set(0, 1.15, -1.0);
    rear.rotation.x = 0.1;

    this._buildSideWindows(carGroup, mats);
  }

  private _buildSideWindows(carGroup: THREE.Group, mats: CarMaterials) {
    const winGeo = new THREE.BoxGeometry(0.05, 0.6, 0.8);
    const swFL = this._createCarPart(carGroup, winGeo, 'glass_side_front_left', 'Front Left Window', mats.window);
    swFL.position.set(0.7, 1.15, 0.5);

    const swFR = this._createCarPart(carGroup, winGeo, 'glass_side_front_right', 'Front Right Window', mats.window);
    swFR.position.set(-0.7, 1.15, 0.5);

    const swRL = this._createCarPart(carGroup, winGeo, 'glass_side_rear_left', 'Rear Left Window', mats.window);
    swRL.position.set(0.7, 1.15, -0.5);

    const swRR = this._createCarPart(carGroup, winGeo, 'glass_side_rear_right', 'Rear Right Window', mats.window);
    swRR.position.set(-0.7, 1.15, -0.5);
  }

  private _buildCarBumpersAndGrill(carGroup: THREE.Group, mats: CarMaterials) {
    const bumperF = this._createCarPart(carGroup, new THREE.BoxGeometry(1.9, 0.35, 0.2), 'bumper_front', 'Front Bumper', mats.bumper);
    bumperF.position.set(0, 0.3, 2.05);

    const bumperR = this._createCarPart(carGroup, new THREE.BoxGeometry(1.9, 0.4, 0.3), 'bumper_rear', 'Rear Bumper', mats.bumper);
    bumperR.position.set(0, 0.3, -2.05);

    const grill = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.4, 0.05), mats.grill);
    grill.position.set(0, 0.6, 2.01);
    carGroup.add(grill);

    const plate = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.15, 0.05), new THREE.MeshStandardMaterial({ color: 0xffffff }));
    plate.position.set(0, 0.3, -2.21);
    carGroup.add(plate);

    this._buildGrillSlats(carGroup);
  }

  private _buildGrillSlats(carGroup: THREE.Group) {
    for (let i = 0; i < 3; i++) {
      const slat = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.05, 0.06), new THREE.MeshStandardMaterial({ color: 0x111111 }));
      slat.position.set(0, 0.5 + i * 0.1, 2.02);
      carGroup.add(slat);
    }
  }

  private _buildCarLights(carGroup: THREE.Group, mats: CarMaterials) {
    const hlGeo = new THREE.CylinderGeometry(0.15, 0.15, 0.1, 16);
    hlGeo.rotateX(Math.PI / 2);

    const hlL = new THREE.Mesh(hlGeo, mats.light.clone());
    hlL.position.set(0.6, 0.6, 2.02);
    this.registerInteractable(hlL, 'headlight_left');
    carGroup.add(hlL);

    const hlR = new THREE.Mesh(hlGeo, mats.light.clone());
    hlR.position.set(-0.6, 0.6, 2.02);
    this.registerInteractable(hlR, 'headlight_right');
    carGroup.add(hlR);

    this._buildTailLight(carGroup, 0.7, 'left', mats.tailLight);
    this._buildTailLight(carGroup, -0.7, 'right', mats.tailLight);
  }

  private _buildTailLight(carGroup: THREE.Group, x: number, side: string, tailLightMat: THREE.Material) {
    const tlGroup = new THREE.Group();
    const tlBox = new THREE.BoxGeometry(0.15, 0.1, 0.05);

    const red = new THREE.Mesh(tlBox, tailLightMat.clone());
    red.position.set(0, 0.1, 0);
    this.registerInteractable(red, `taillight_${side}_brake`);
    tlGroup.add(red);

    const amber = new THREE.Mesh(tlBox, new THREE.MeshStandardMaterial({ color: 0xffaa00, emissive: 0xffaa00, emissiveIntensity: 0.5 }));
    amber.position.set(0, 0, 0);
    this.registerInteractable(amber, `taillight_${side}_turn`);
    tlGroup.add(amber);

    const white = new THREE.Mesh(tlBox, new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xaaaaaa, emissiveIntensity: 0.5 }));
    white.position.set(0, -0.1, 0);
    this.registerInteractable(white, `taillight_${side}_reverse`);
    tlGroup.add(white);

    tlGroup.position.set(x, 0.6, -2.01);
    carGroup.add(tlGroup);
  }

  private _buildCarWheels(carGroup: THREE.Group, mats: CarMaterials) {
    const wheelRadius = 0.35;
    const wheelWidth = 0.25;
    const wheelGeo = new THREE.CylinderGeometry(wheelRadius, wheelRadius, wheelWidth, 16);
    wheelGeo.rotateZ(Math.PI / 2);

    this._createWheel(carGroup, wheelGeo, 0.9, 1.2, 'wheel_front_left', mats.tire, mats.rim);
    this._createWheel(carGroup, wheelGeo, -0.9, 1.2, 'wheel_front_right', mats.tire, mats.rim);
    this._createWheel(carGroup, wheelGeo, 0.9, -1.2, 'wheel_rear_left', mats.tire, mats.rim);
    this._createWheel(carGroup, wheelGeo, -0.9, -1.2, 'wheel_rear_right', mats.tire, mats.rim);
  }

  private _createWheel(carGroup: THREE.Group, geo: THREE.CylinderGeometry, x: number, z: number, id: string, tireMat: THREE.Material, rimMat: THREE.Material) {
    const wGroup = new THREE.Group();
    const tire = new THREE.Mesh(geo, tireMat.clone());
    const rim = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.21, 0.27, 8).rotateZ(Math.PI / 2), rimMat);
    wGroup.add(tire);
    wGroup.add(rim);
    wGroup.position.set(x, 0.35, z);
    this.registerInteractable(tire, id);
    carGroup.add(wGroup);
  }

  private _buildCarAccessories(carGroup: THREE.Group) {
    const handleMat = new THREE.MeshStandardMaterial({ color: 0xbdc3c7 });
    const hL = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.2), handleMat);
    hL.position.set(0.95, 0.7, 0.2);
    carGroup.add(hL);

    const hR = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.2), handleMat);
    hR.position.set(-0.95, 0.7, 0.2);
    carGroup.add(hR);

    const mirrorMat = new THREE.MeshStandardMaterial({ color: 0x2c3e50 });
    const mL = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.15, 0.1), mirrorMat);
    mL.position.set(1.0, 0.9, 0.8);
    carGroup.add(mL);

    const mR = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.15, 0.1), mirrorMat);
    mR.position.set(-1.0, 0.9, 0.8);
    carGroup.add(mR);
  }

  /* -------------------- INTERACTION -------------------- */
  private onMouseClick(event: MouseEvent) {
    if (this.readOnly()) return; // Ignorar clics si es de solo lectura.
    this.updateMouseCoords(event);
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const hit = this.raycaster.intersectObjects(this.interactableObjects, true)[0];
    if (hit) this._togglePartSelection(hit.object.userData['id']);
  }

  private _togglePartSelection(id: string) {
    if (this.selectedPartIds.has(id)) this.selectedPartIds.delete(id);
    else this.selectedPartIds.add(id);
    this.updateColors();
    this.partsSelected.emit([...this.selectedPartIds]);
  }

  private onMouseMove(event: MouseEvent) {
    if (this.readOnly()) {
      this.renderer.domElement.style.cursor = 'default';
      return;
    }
    this.updateMouseCoords(event);
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const hits = this.raycaster.intersectObjects(this.interactableObjects, true);
    const hovered = hits[0]?.object;
    this._resetNonSelectedColors();
    this._applyHoverStyle(hovered);
  }

  private _resetNonSelectedColors() {
    this.interactableObjects.forEach(obj => {
      const id = obj.userData['id'];
      const mat = (obj as THREE.Mesh).material as THREE.MeshStandardMaterial;
      if (mat) {
        mat.color.setHex(this.selectedPartIds.has(id) ? this.selectedColor : obj.userData['originalColor']);
      }
    });
  }

  private _applyHoverStyle(hovered: THREE.Object3D | undefined) {
    if (hovered && !this.selectedPartIds.has(hovered.userData['id'])) {
      const mat = (hovered as THREE.Mesh).material as THREE.MeshStandardMaterial;
      if (mat) mat.color.setHex(this.hoverColor);
      this.renderer.domElement.style.cursor = 'pointer';
    } else {
      this.renderer.domElement.style.cursor = 'default';
    }
  }

  private updateMouseCoords(event: MouseEvent) {
    const rect = this.rendererContainer.nativeElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  private registerInteractable(object: THREE.Object3D, id: string) {
    object.userData = { ...object.userData, id };
    const mat = (object as THREE.Mesh).material as THREE.MeshStandardMaterial;
    if (mat && mat.color) {
      object.userData['originalColor'] = mat.color.getHex();
    }
    this.interactableObjects.push(object);
  }

  private updateColors() {
    this.interactableObjects.forEach(obj => {
      const id = obj.userData['id'];
      const mat = (obj as THREE.Mesh).material as THREE.MeshStandardMaterial;
      if (mat) {
        mat.color.setHex(this.selectedPartIds.has(id) ? this.selectedColor : obj.userData['originalColor']);
      }
    });
  }

  /* -------------------- IMAGE EXPORT -------------------- */
  public captureImage(): string {
    this.renderer.render(this.scene, this.camera);
    return this.renderer.domElement.toDataURL('image/png');
  }

  /* -------------------- ANIMATION LOOP -------------------- */
  private animate = () => {
    this.animationId = requestAnimationFrame(this.animate);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  };

  private onWindowResize() {
    const container = this.rendererContainer.nativeElement;
    const w = container.clientWidth;
    const h = container.clientHeight;

    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  private disposeScene(scene: THREE.Object3D) {
    scene.traverse((obj: any) => this._disposeObject(obj));
  }

  private _disposeObject(obj: any) {
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) this._disposeMaterial(obj.material);
  }

  private _disposeMaterial(material: any) {
    const mats = Array.isArray(material) ? material : [material];
    mats.forEach((m: THREE.Material) => m.dispose());
  }
}